import { useCallback, useEffect, useState } from 'react';
import type {
  AssistantActionProposal,
  AssistantAttachment,
  AssistantReply,
  AssistantStatus,
} from '@ddp/shared';

import { apiFetch } from '../../api/client.js';
import { track } from '../analytics/analytics.js';

export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** Titre de la vidéo jointe au message, pour l'afficher dans le fil (ADR-0027). */
  attachmentTitle?: string;
  /** Actions proposées par l'assistant, à confirmer par l'utilisateur (ADR-0027, incrément 2). */
  proposals?: AssistantActionProposal[];
}

interface AssistantState {
  status: AssistantStatus | null;
  messages: AssistantMessage[];
  sending: boolean;
  error: string | null;
  send: (text: string, attachment?: AssistantAttachment | null) => Promise<void>;
}

/**
 * État de l'assistant IA (ADR-0016). Interroge d'abord `/assistant/status` pour savoir s'il est
 * actif — le panneau affiche « à venir » sans tenter d'envoyer tant qu'il est dormant.
 */
export function useAssistant(): AssistantState {
  const [status, setStatus] = useState<AssistantStatus | null>(null);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void apiFetch<AssistantStatus>('/assistant/status')
      .then((s) => active && setStatus(s))
      // Un statut illisible est traité comme dormant : jamais de faux « actif ».
      .catch(() => active && setStatus({ enabled: false, provider: null }));
    return () => {
      active = false;
    };
  }, []);

  const send = useCallback(async (text: string, attachment?: AssistantAttachment | null) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setError(null);
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'user',
        text: trimmed,
        attachmentTitle: attachment?.title,
      },
    ]);
    setSending(true);
    // Analytique (ADR-0026) : on mesure qu'une question a été posée, jamais son contenu.
    track('ASSISTANT_QUERIED');
    try {
      // Seul l'id de la vidéo voyage ; le serveur revalide l'appartenance et résout le titre (ADR-0027).
      const reply = await apiFetch<AssistantReply>('/assistant/messages', {
        method: 'POST',
        body: { message: trimmed, videoId: attachment?.videoId },
      });
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          text: reply.reply,
          proposals: reply.proposals,
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Assistant indisponible');
    } finally {
      setSending(false);
    }
  }, []);

  return { status, messages, sending, error, send };
}
